import {getTestProps} from '@benchling/js-test-utils';
import {createStyles, Divider, Popover, Stack, Text, UnstyledButton} from '@mantine/core';
import {useDisclosure} from '@mantine/hooks';
import assert from 'assert';
import React, {useCallback, useRef, useState} from 'react';

import {trackEvent} from 'analytics/Analytics';
import {ELN_ANALYTICS_ACTIONS} from 'constants/analytics-products';
import {AddStructuredTableRowsUsingAIResult} from 'notebook/ai-tools/flexible-data-entry/addStructuredTableRowsUsingAI';
// @ts-expect-error
import * as selutil from 'notebook/mediocre/selection/text/SelUtil';
import {StructuredTable} from 'notebook/structured-tables/StructuredTableConstants';
import {getEntryOrFileIdKey} from 'notebook/util/analytics/FileAnalyticsUtils';
import notifications from 'notifications';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import useIsNotUnmounted from 'ui-library/hooks/useIsNotUnmounted';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import * as logging from 'util/Logging';

import AIDataEntryPopover from './AIDataEntryPopover';
import {FlexibleDataEntryExperience} from './NotebookAIToolsConstants';
import {
  openCustomPlotDraftInChat,
  runSuggestPlotPromptInChat,
} from './suggest-plots/runSuggestPlotPromptInChat';
import {getIconForChartType, PlotSuggestion} from './suggest-plots/SuggestPlotsConstants';
import {suggestPlotsForTable} from './suggest-plots/SuggestPlotsDAO';
import {
  fadeInKeyframes,
  LoadingShimmerList,
  spinKeyframes,
} from './suggest-plots/SuggestPlotsLoadingComponents';

export interface AITableMenuButtonProps {
  disabled?: boolean;
  entryId: string;
  entryName?: string | null;
  tableName: string;
  dataTestAttrs?: DataTestAttrs;
  showDataEntry?: boolean;
  showSuggestedPlots?: boolean;
  getTableData?: () => {columns: Array<string>; rows: Array<Array<string>>};
  structuredTable?: StructuredTable<unknown, unknown>;
  undoState?: (selectionRestoreData: {selection: SelectionRestoreData}) => void;
  addAIDataEntryRows?: (
    initialPromptText?: string,
    initialSelectedTableName?: string,
    result?: AddStructuredTableRowsUsingAIResult | null
  ) => void;
}

export default function AITableMenuButton({
  disabled = false,
  entryId,
  entryName = null,
  tableName,
  dataTestAttrs,
  showDataEntry = false,
  showSuggestedPlots = false,
  getTableData,
  structuredTable,
  undoState,
  addAIDataEntryRows,
}: AITableMenuButtonProps): JSX.Element {
  const {classes} = useStyles();
  if (showDataEntry) {
    assert(structuredTable != null, 'structuredTable is required when showDataEntry is true');
    assert(undoState != null, 'undoState is required when showDataEntry is true');
    assert(addAIDataEntryRows != null, 'addAIDataEntryRows is required when showDataEntry is true');
  }

  const [menuOpened, {open: openMenu, close: closeMenu}] = useDisclosure(false);
  const [dataEntryOpen, setDataEntryOpen] = useState(false);
  const [hasSuccessfulSubmission, setHasSuccessfulSubmission] = useState(false);

  const [isLoadingPlots, setIsLoadingPlots] = useState(false);
  const [suggestions, setSuggestions] = useState<Array<PlotSuggestion>>([]);
  const [plotErrorMessage, setPlotErrorMessage] = useState<string | null>(null);
  const [launchingSuggestionKey, setLaunchingSuggestionKey] = useState<string | null>(null);
  const requestIdRef = useRef(0);
  const mountedRef = useIsNotUnmounted();
  const getTableDataRef = useRef(getTableData);
  getTableDataRef.current = getTableData;

  const fetchSuggestions = useCallback(async (): Promise<void> => {
    if (!getTableDataRef.current) {
      return;
    }
    const {columns, rows} = getTableDataRef.current();
    if (rows.length < 3) {
      setSuggestions([]);
      setPlotErrorMessage('Add more data to get plot suggestions.');
      return;
    }
    const requestId = ++requestIdRef.current;
    setIsLoadingPlots(true);
    setPlotErrorMessage(null);
    setSuggestions([]);
    try {
      const response = await suggestPlotsForTable({
        entryName,
        tableName,
        columns,
        rows,
      });
      if (!mountedRef.current || requestIdRef.current !== requestId) {
        return;
      }
      if (response.hadError || response.suggestions.length === 0) {
        setPlotErrorMessage(
          response.errorMessage ?? 'No suggestions for this table. Try the Custom option below.'
        );
        setSuggestions([]);
      } else {
        setSuggestions(response.suggestions);
        setPlotErrorMessage(null);
      }
    } catch (err) {
      if (!mountedRef.current || requestIdRef.current !== requestId) {
        throw err;
      }
      logging.error(err, {
        extra: {message: 'AITableMenuButton failed to fetch plot suggestions'},
      });
      setPlotErrorMessage('An error ocurred while trying to generate plot suggestions.');
    } finally {
      if (mountedRef.current && requestIdRef.current === requestId) {
        setIsLoadingPlots(false);
      }
    }
  }, [entryName, tableName, mountedRef]);

  const handleMenuToggle = useCallback(() => {
    if (disabled || dataEntryOpen) {
      return;
    }
    if (menuOpened) {
      closeMenu();
      return;
    }
    openMenu();
    if (showSuggestedPlots) {
      fetchSuggestions().catch(() => undefined);
    }
  }, [disabled, dataEntryOpen, menuOpened, openMenu, closeMenu, showSuggestedPlots, fetchSuggestions]);

  const handleDataEntryClick = useCallback(() => {
    closeMenu();
    trackEvent(ELN_ANALYTICS_ACTIONS.CLICKED_FLEXIBLE_DATA_ENTRY_POPOVER_ICON, {
      [getEntryOrFileIdKey(entryId, true)]: entryId,
      experience: FlexibleDataEntryExperience.POPOVER,
    });
    setDataEntryOpen(true);
  }, [closeMenu, entryId]);

  const handleDataEntryCancel = useCallback(() => {
    setDataEntryOpen(false);
    trackEvent(ELN_ANALYTICS_ACTIONS.CLOSED_FLEXIBLE_DATA_ENTRY_MODAL, {
      experience: FlexibleDataEntryExperience.POPOVER,
      numFiles: 0,
      [getEntryOrFileIdKey(entryId, true)]: entryId,
    });
    if (hasSuccessfulSubmission) {
      trackEvent(ELN_ANALYTICS_ACTIONS.ACCEPTED_FLEXIBLE_DATA_ENTRY_RESULT, {
        numFiles: 0,
        wasVerified: null,
        entryId,
        experience: FlexibleDataEntryExperience.POPOVER,
        isMultiTable: false,
      });
      setHasSuccessfulSubmission(false);
    }
  }, [entryId, hasSuccessfulSubmission]);

  const handleUndo = useCallback(() => {
    const selectionData = selutil.getSelectionIndices();
    undoState!({selection: selectionData});
  }, [undoState]);

  const handleSubmit = useCallback(
    (result: AddStructuredTableRowsUsingAIResult | null): void => {
      addAIDataEntryRows!(undefined, undefined, result);
    },
    [addAIDataEntryRows]
  );

  const launchChatAction = useCallback(
    async (key: string, action: () => Promise<unknown>) => {
      if (launchingSuggestionKey != null) {
        return;
      }
      setLaunchingSuggestionKey(key);
      try {
        await action();
        closeMenu();
      } catch (err) {
        if (!mountedRef.current) {
          throw err;
        }
        logging.error(err, {
          extra: {message: `AITableMenuButton failed: ${key}`},
        });
        notifications.error('Could not open AI chat. Please try again.');
      } finally {
        if (mountedRef.current) {
          setLaunchingSuggestionKey(null);
        }
      }
    },
    [launchingSuggestionKey, closeMenu, mountedRef]
  );

  const handleSuggestionClick = useCallback(
    (suggestion: PlotSuggestion) => {
      const idx = suggestions.indexOf(suggestion);
      const key = `${idx}-${suggestion.title}`;
      return launchChatAction(key, () =>
        runSuggestPlotPromptInChat({suggestion, tableName, entryId, entryName})
      );
    },
    [suggestions, tableName, entryId, entryName, launchChatAction]
  );

  const handleCustomClick = useCallback(
    () => launchChatAction('custom', () => openCustomPlotDraftInChat({tableName, entryId, entryName})),
    [tableName, entryId, entryName, launchChatAction]
  );

  const isOpen = menuOpened || dataEntryOpen;

  const handleClose = useCallback(() => {
    if (dataEntryOpen) {
      handleDataEntryCancel();
    } else {
      closeMenu();
    }
  }, [dataEntryOpen, handleDataEntryCancel, closeMenu]);

  return (
    <Popover
      opened={isOpen}
      onClose={handleClose}
      position={dataEntryOpen ? 'top' : 'bottom-start'}
      withArrow={true}
      shadow="md"
      radius="md"
      withinPortal={true}
      offset={8}
    >
      <Popover.Target>
        <UnstyledButton
          className={classes.trigger}
          onClick={handleMenuToggle}
          disabled={disabled}
          aria-label="AI tools"
          {...getTestProps(dataTestAttrs)}
        >
          <BenchlingIcon icon="sparkle" className={classes.triggerIcon} />
        </UnstyledButton>
      </Popover.Target>
      <Popover.Dropdown p={0}>
        {dataEntryOpen ? (
          <AIDataEntryPopover
            entryId={entryId}
            structuredTable={structuredTable!}
            onSubmit={handleSubmit}
            onCancel={handleDataEntryCancel}
            onUndo={handleUndo}
            hasSuccessfulSubmission={hasSuccessfulSubmission}
            setHasSuccessfulSubmission={setHasSuccessfulSubmission}
            addAIDataEntryRows={addAIDataEntryRows!}
          />
        ) : (
          <MenuContent
            classes={classes}
            showDataEntry={showDataEntry}
            showSuggestedPlots={showSuggestedPlots}
            isLoadingPlots={isLoadingPlots}
            suggestions={suggestions}
            plotErrorMessage={plotErrorMessage}
            launchingSuggestionKey={launchingSuggestionKey}
            onDataEntryClick={handleDataEntryClick}
            onSuggestionClick={handleSuggestionClick}
            onCustomClick={handleCustomClick}
          />
        )}
      </Popover.Dropdown>
    </Popover>
  );
}

function MenuContent({
  classes,
  showDataEntry,
  showSuggestedPlots,
  isLoadingPlots,
  suggestions,
  plotErrorMessage,
  launchingSuggestionKey,
  onDataEntryClick,
  onSuggestionClick,
  onCustomClick,
}: {
  classes: ReturnType<typeof useStyles>['classes'];
  showDataEntry: boolean;
  showSuggestedPlots: boolean;
  isLoadingPlots: boolean;
  suggestions: Array<PlotSuggestion>;
  plotErrorMessage: string | null;
  launchingSuggestionKey: string | null;
  onDataEntryClick: () => void;
  onSuggestionClick: (suggestion: PlotSuggestion) => void;
  onCustomClick: () => void;
}): JSX.Element {
  const sections: Array<React.ReactNode> = [];

  if (showDataEntry) {
    sections.push(
      <UnstyledButton
        key="data-entry"
        className={classes.menuItem}
        onClick={onDataEntryClick}
        {...getTestProps({component: 'AITableMenu', element: 'dataEntryItem'})}
      >
        <div className={classes.menuItemIcon}>
          <BenchlingIcon icon="table-rows" />
        </div>
        <div className={classes.menuItemText}>
          <Text size="sm" weight={600}>
            Add rows with AI
          </Text>
          <Text size="xs" color="dimmed">
            Upload files or type a prompt
          </Text>
        </div>
      </UnstyledButton>
    );
  }

  if (showSuggestedPlots) {
    if (sections.length > 0) {
      sections.push(<Divider key="divider" my={4} />);
    }
    sections.push(
      <div key="suggested-plots" className={classes.suggestedPlotsSection}>
        <Text size="xs" weight={600} color="dimmed" className={classes.sectionLabel}>
          Suggested plots
        </Text>
        <SuggestedPlotsContent
          isLoading={isLoadingPlots}
          suggestions={suggestions}
          errorMessage={plotErrorMessage}
          onSuggestionClick={onSuggestionClick}
          onCustomClick={onCustomClick}
          launchingSuggestionKey={launchingSuggestionKey}
        />
      </div>
    );
  }

  return <div className={classes.menuRoot}>{sections}</div>;
}

function SuggestedPlotsContent({
  isLoading,
  suggestions,
  errorMessage,
  onSuggestionClick,
  onCustomClick,
  launchingSuggestionKey,
}: {
  isLoading: boolean;
  suggestions: Array<PlotSuggestion>;
  errorMessage: string | null;
  onSuggestionClick: (suggestion: PlotSuggestion) => void;
  onCustomClick: () => void;
  launchingSuggestionKey: string | null;
}): JSX.Element {
  const {classes} = useStyles();
  const isLaunchingChat = launchingSuggestionKey != null;
  const isCustomLaunching = launchingSuggestionKey === 'custom';
  const displayedSuggestions = suggestions;

  return (
    <Stack spacing="xs">
      {isLoading ? (
        <LoadingShimmerList />
      ) : errorMessage != null ? (
        <Text size="xs" color="orange" className={classes.plotError}>
          {errorMessage}
        </Text>
      ) : (
        displayedSuggestions.map((suggestion, idx) => {
          const key = `${idx}-${suggestion.title}`;
          const isLaunching = isLaunchingChat && launchingSuggestionKey === key;
          return (
            <UnstyledButton
              key={key}
              className={classes.plotSuggestion}
              onClick={() => {
                onSuggestionClick(suggestion);
              }}
              disabled={isLaunchingChat}
              style={{animationDelay: `${idx * 60}ms`}}
            >
              <div className={classes.plotSuggestionIcon}>
                <BenchlingIcon icon={getIconForChartType(suggestion.chartType)} />
              </div>
              <div className={classes.plotSuggestionText}>
                <Text size="sm" weight={600} lineClamp={1}>
                  {suggestion.title}
                </Text>
                <Text size="xs" color="dimmed" lineClamp={1}>
                  {suggestion.description}
                </Text>
              </div>
              <div className={classes.chevron}>
                <BenchlingIcon
                  icon={isLaunching ? 'spinner-third' : 'arrow-right'}
                  className={isLaunching ? classes.spin : undefined}
                />
              </div>
            </UnstyledButton>
          );
        })
      )}
      <UnstyledButton
        className={classes.plotSuggestion}
        onClick={onCustomClick}
        disabled={isLaunchingChat}
        style={{animationDelay: `${displayedSuggestions.length * 60}ms`}}
      >
        <div className={classes.plotSuggestionIcon}>
          <BenchlingIcon icon="chart-line" />
        </div>
        <div className={classes.plotSuggestionText}>
          <Text size="sm" weight={600}>
            Custom
          </Text>
          <Text size="xs" color="dimmed" lineClamp={1}>
            Describe your own chart
          </Text>
        </div>
        <div className={classes.chevron}>
          <BenchlingIcon
            icon={isCustomLaunching ? 'spinner-third' : 'arrow-right'}
            className={isCustomLaunching ? classes.spin : undefined}
          />
        </div>
      </UnstyledButton>
    </Stack>
  );
}

const useStyles = createStyles((theme) => ({
  trigger: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 22,
    height: 22,
    cursor: 'pointer',
    borderRadius: theme.radius.sm,
    transition: 'background-color 140ms ease, color 140ms ease',
    '&:hover:not(:disabled)': {
      backgroundColor: theme.colors.violet[0],
      color: theme.colors.violet[8],
    },
    '&:disabled': {
      color: theme.colors.gray[5],
      cursor: 'not-allowed',
    },
  },
  triggerIcon: {
    height: 12,
    color: theme.colors.violet[7],
  },
  menuRoot: {
    display: 'flex',
    flexDirection: 'column',
    width: 340,
    padding: theme.spacing.xs,
  },
  menuItem: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing.sm,
    padding: theme.spacing.sm,
    borderRadius: theme.radius.sm,
    transition: 'background-color 140ms ease',
    '&:hover': {
      backgroundColor: theme.colors.gray[0],
    },
  },
  menuItemIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 28,
    height: 28,
    borderRadius: theme.radius.sm,
    flexShrink: 0,
    backgroundColor: theme.colors.violet[0],
    color: theme.colors.violet[7],
  },
  menuItemText: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    flex: 1,
    minWidth: 0,
  },
  suggestedPlotsSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing.xs,
  },
  sectionLabel: {
    paddingLeft: theme.spacing.sm,
    paddingTop: theme.spacing.xs,
  },
  plotError: {
    padding: theme.spacing.sm,
  },
  plotSuggestion: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing.sm,
    padding: theme.spacing.sm,
    borderRadius: theme.radius.sm,
    height: 48,
    transition: 'background-color 140ms ease, transform 140ms ease',
    opacity: 0,
    animation: `${fadeInKeyframes} 260ms ease forwards`,
    '&:hover:not(:disabled)': {
      backgroundColor: theme.colors.violet[0],
      transform: 'translateY(-1px)',
    },
    '&:disabled': {
      cursor: 'default',
      opacity: 0.6,
    },
  },
  plotSuggestionIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 24,
    height: 24,
    borderRadius: theme.radius.sm,
    flexShrink: 0,
    backgroundColor: theme.colors.violet[0],
    color: theme.colors.violet[7],
    fontSize: theme.fontSizes.sm,
  },
  plotSuggestionText: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    flex: 1,
    minWidth: 0,
  },
  chevron: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: theme.colors.gray[6],
    paddingTop: 2,
  },
  spin: {
    animation: `${spinKeyframes} 1s linear infinite`,
    display: 'inline-block',
  },
}));
